import axios from "axios";
import authService from "./authService";
const baseURL = "/enodo/";

const apiClient = axios.create({
  baseURL: baseURL, // FastAPI URL
  withCredentials: true,
});

class AuthError extends Error {
  constructor(message, code, originalError) {
    super(message);
    this.name = "AuthError";
    this.code = code; // e.g., "NOT_LOGGED_IN_VUO"
    this.status = 401;
    this.originalRequest = originalError.config;
  }
}

// Add interceptor to handle authentication errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If the error is 401 or 403, clear the user from localStorage
    // This ensures the user is logged out if their session becomes invalid
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      console.warn(
        "[apiClient] Authentication error - clearing user from localStorage",
      );
      authService.saveUser(null);
    }
    return Promise.reject(
      new AuthError("Not correctly logged in", "AUTH_FAIL", ""),
    );
  },
);

export default {
  baseURL,
  getMessage() {
    return apiClient.get("/");
  },
  getConnectedUser() {
    return apiClient.get("/api/AmIVuoLogged/", {
      "Content-Type": "application/json",
    });
  },
  getDevelopment() {
    return apiClient.get("/api/development/", {
      "Content-Type": "application/json",
    });
  },
  getFakeVUO() {
    return apiClient.get("/api/fake_vuo/", {
      "Content-Type": "application/json",
    });
  },
  getUsers(query_filter) {
    return apiClient.get(
      `/api/authors/?query_filter=${encodeURIComponent(query_filter)}`,
      { "Content-Type": "application/json" },
    );
  },
  getPreview(formData, onUploadProgress) {
    return apiClient.post(`/api/preview/`, formData, {
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onUploadProgress(percentCompleted);
        }
      },
    });
  },
  // setFrontendLogging() {
  //   return apiClient.get("/api/setFrontendLogging", {
  //     "Content-Type": "application/json",
  //   });
  // },
  getPreviewStatus() {
    return apiClient.get(`/api/preview/status/`, {
      "Content-Type": "application/json",
    });
  },
  sendSubmission(formData) {
    console.debug("[apiClient.sendSubmission] firing once");
    return apiClient.post(`/api/confirm_upload/`, formData, {
      "Content-Type": "multipart/form-data",
    });
  },
  getAddDatasetPreview(investigationId, formData, onUploadProgress) {
    return apiClient.post(
      `/api/investigation/${investigationId}/dataset/preview`,
      formData,
      {
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onUploadProgress(percentCompleted);
          }
        },
      },
    );
  },
  confirmAddDataset(investigationId, description) {
    const formData = new FormData();
    formData.append("description", description);
    return apiClient.post(
      `/api/investigation/${investigationId}/dataset/confirm`,
      formData,
      { "Content-Type": "multipart/form-data" },
    );
  },
  getInvestigationInfoByToken(token, small_info = false) {
    return apiClient.get(`/api/investigation/${token}`, {
      params: {
        small_info: small_info,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });
  },
  getInvestigationInfoByID(id, small_info = false) {
    return apiClient.get(`/api/investigationByID/${id}`, {
      params: {
        small_info: small_info,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });
  },
  getInvestigationFiles(investigationId, investigation_name, version = null) {
    return apiClient.get(`/api/investigation/${investigationId}/files?`, {
      params: {
        investigation_name: investigation_name,
        ...(version === null ? {} : { version }),
      },
    });
  },
  getListInvestigation() {
    return apiClient.get(`/api/investigation/`, {
      "Content-Type": "application/json",
    });
  },
  getMyInvestigationList() {
    return apiClient.get(`/api/MyInvestigation/`, {
      "Content-Type": "application/json",
    });
  },
  downloadInvestigation(id, investigation_name) {
    return apiClient.get(
      `/api/download_investigation/${id}`,
      {
        params: {
          investigation_name: investigation_name,
        },
      },
      {
        responseType: "blob", // Use this to handle file downloads
      },
    );
  },
  // Admin endpoints
  getDashboardStats(type = "overview") {
    return apiClient.get(`/api/admin/dashboard/stats/${type}`, {
      "Content-Type": "application/json",
    });
  },
  getAdminLogs(params = {}) {
    return apiClient.get(`/api/admin/logs`, {
      params,
      "Content-Type": "application/json",
    });
  },
  searchUsers(query) {
    return apiClient.get(`/api/admin/users/search`, {
      params: { q: query },
      "Content-Type": "application/json",
    });
  },
  // User management endpoints
  getAdminUsers() {
    return apiClient.get(`/api/admin/users`, {
      "Content-Type": "application/json",
    });
  },
  getActiveUsersToday() {
    return apiClient.get(`/api/admin/users/active-today`, {
      "Content-Type": "application/json",
    });
  },
  getConcurrentUsers() {
    return apiClient.get(`/api/admin/users/concurrent`, {
      "Content-Type": "application/json",
    });
  },
  updateUserRole(userId, action) {
    return apiClient.post(
      `/api/admin/users/${userId}/${action}`,
      {},
      {
        "Content-Type": "application/json",
      },
    );
  },
  logout() {
    return apiClient.post("/api/logout/");
  },
  // Strip the /enodo prefix so apiClient (baseURL="/enodo/") doesn't double it.
  // fileUrl values come pre-built with /enodo/ for direct browser use (downloads etc.)
  getFile(path, config = {}) {
    return apiClient.get(path.replace(/^\/enodo/, ""), config);
  },
  getImage(path, config = {}) {
    return apiClient.get(path.replace(/^\/enodo/, ""), config);
  },
  getPdf(path, config = {}) {
    return apiClient.get(path.replace(/^\/enodo/, ""), config);
  },
  getTiffInfo(fileUrl) {
    return apiClient.get(
      fileUrl.replace("/file?", "/tiff-info?").replace(/^\/enodo/, ""),
    );
  },
  getTiffPage(fileUrl, page) {
    return apiClient.get(
      fileUrl.replace("/file?", "/tiff-page?").replace(/^\/enodo/, "") +
        `&page=${page}`,
      { responseType: "arraybuffer" },
    );
  },
};
